import '../utils/request-idle-callback-shim';
import { i18n } from '../utils/i18n';
import DestinationModal from './DestinationModal';
import AirportModal from './AirportModal';
import DurationModal from './DurationModal';
import DateModal from './DateModal';
import RoomModal from './RoomModal';
import debounce from '../utils/debounce';
import getMinMaxDuration from '../utils/get-minmax-duration';
import {
  readRememberedSearch,
  writeRememberedSearch,
  type RememberedDates,
  type RememberedSearch,
} from '../utils/remembered-search-store';
import {
  applyRememberedSearch,
  withdrawRememberedSearch,
} from './apply-remembered-search';
import { resultsQuery } from '../utils/results-query';
import {
  fallBackUnavailableCriteria,
  type DroppedCriterion,
} from './fall-back-unavailable-criteria';
import type {
  ProductGroup,
  AirportGroup,
  DateThresholds,
  Travellers,
  Duration,
} from '@search/api/src/types';

/** Each criterion a Remembered Search can restore. */
type RememberedCriterion = DroppedCriterion | 'rooms';

const MOBILE_BREAKPOINT = '(max-width: 1023px)';
const SCROLL_COLLAPSE_THRESHOLD = 50;

/**
 * A `YYYY-MM-DD` (or ISO date-time) string as local midnight on that day.
 * `new Date('2026-07-01')` is UTC midnight, which local getters read as the
 * previous day anywhere behind UTC.
 */
const parseLocalDate = (value: string): Date => {
  const [year, month, day] = value.split('T')[0].split('-').map(Number);
  return new Date(year, month - 1, day);
};

interface Filters {
  destinations: Array<ProductGroup>;
  airports: Array<AirportGroup>;
  durations: Duration;
  dateThresholds: DateThresholds;
  occupancyThresholds: Travellers;
}

class SearchComponent {
  private readonly destinationButton: HTMLElement;
  private readonly airportButton: HTMLElement;
  private readonly durationButton: HTMLElement;
  private readonly dateButton: HTMLElement;
  private readonly roomsButton: HTMLElement;
  private readonly searchButton: HTMLElement;
  private readonly toggleSearchButton: HTMLElement;
  private readonly configOptions: string[];

  private readonly destinationModal: DestinationModal;
  private readonly airportModal: AirportModal;
  private readonly durationModal: DurationModal;
  private readonly dateModal: DateModal;
  private readonly roomModal: RoomModal;

  private isOpen: boolean;
  private initialized = false;

  /**
   * The remembered criteria dropped on load because the page could no longer
   * offer them: those its search filters no longer offer (which fell back to
   * the page's own value), then `'dates'` when remembered dates had gone
   * stale — the "dropped" list the Remembered Search Applied event reports.
   */
  public readonly droppedCriteria: DroppedCriterion[] = [];

  /** The query string the Search link currently searches. */
  private queryString = '';

  /**
   * The query string the applied Remembered Search produced, pinned criteria
   * included — searching it unchanged is reported as `prefilled`. Null when
   * nothing was remembered.
   */
  private prefilledQueryString: string | null = null;

  /** Re-reads the departure window with the shortest stay now selected. */
  private readonly refreshDateWindow: () => void;

  /** Guards against overriding manual user toggle with scroll behaviour. */
  private userToggledManually = false;
  /** Highest scroll-depth percentage bucket already reported to analytics. */
  private maxScrollDepthReported = 0;
  private readonly mobileQuery: MediaQueryList;
  private readonly container: HTMLElement;
  private readonly backdrop: HTMLElement | null;

  constructor(container: HTMLElement, filters: Filters) {
    const searchComponentEl = container.querySelector(
      '.search-component'
    ) as HTMLElement;
    this.configOptions = JSON.parse(
      searchComponentEl?.dataset.searchOptions || '[]'
    );

    this.destinationButton = container.querySelector(
      'button[data-search-destination]'
    ) as HTMLElement;
    this.airportButton = container.querySelector(
      'button[data-search-airport]'
    ) as HTMLElement;
    this.durationButton = container.querySelector(
      'button[data-search-duration]'
    ) as HTMLElement;
    this.dateButton = container.querySelector(
      'button[data-search-date]'
    ) as HTMLElement;
    this.roomsButton = container.querySelector(
      'button[data-search-room]'
    ) as HTMLElement;
    this.searchButton = container.querySelector(
      'a[data-search-button]'
    ) as HTMLElement;
    this.toggleSearchButton = container.querySelector(
      'button[data-search-toggle]'
    ) as HTMLElement;
    this.isOpen = this.toggleSearchButton.classList.contains('underline');
    this.container = container;
    this.mobileQuery = window.matchMedia(MOBILE_BREAKPOINT);
    this.backdrop = container.parentElement?.querySelector(
      '[data-search-backdrop]'
    ) as HTMLElement | null;

    // Initialize modals
    this.destinationModal = new DestinationModal(
      filters.destinations,
      selected => {
        this.updateDestinationButton(selected);
      }
    );

    this.airportModal = new AirportModal(filters.airports, selected => {
      this.updateAirportButton(selected);
    });

    const getMinDuration = () => {
      const minDurationString = this.durationButton.dataset.selectedDuration;
      const minDuration = minDurationString
        ? getMinMaxDuration(minDurationString)?.min || 7
        : 7;
      return minDuration;
    };

    // departureDate.min/max are optional|null in the schema; fall back to "now"
    // when absent so the date picker always receives a valid Date.
    // Parse as local midnight to avoid UTC offset issues — new Date("2026-07-01")
    // creates UTC midnight which shifts backward in timezones ahead of UTC.
    const toThresholdDate = (value: string | null | undefined): Date =>
      value ? parseLocalDate(value) : new Date();

    this.durationModal = new DurationModal(
      filters.durations.values ?? [],
      selected => {
        this.updateDurationButton(selected);
        this.dateModal.updateOptions({
          minDate: toThresholdDate(filters.dateThresholds.min),
          maxDate: toThresholdDate(filters.dateThresholds.max),
          minDuration: getMinDuration(),
        });
      }
    );

    this.dateModal = new DateModal(
      {
        minDate: toThresholdDate(filters.dateThresholds.min),
        maxDate: toThresholdDate(filters.dateThresholds.max),
        minDuration: getMinDuration(),
      },
      selected => {
        this.updateDateButton(selected);
      }
    );
    this.refreshDateWindow = () =>
      this.dateModal.updateOptions({
        minDate: toThresholdDate(filters.dateThresholds.min),
        maxDate: toThresholdDate(filters.dateThresholds.max),
        minDuration: getMinDuration(),
      });

    // occupancyThresholds (Travellers) fields are all optional|null in the
    // schema, but RoomModal's RoomConfig requires them. The API normally
    // supplies them; fall back to standard occupancy limits when absent so the
    // room selector is always fully configured (object literal is structurally
    // checked against RoomConfig — no cast, no masking).
    const t = filters.occupancyThresholds;
    this.roomModal = new RoomModal(
      {
        min: t.min ?? 1,
        max: t.max ?? 9,
        adults: {
          min: t.adults?.min ?? 1,
          max: t.adults?.max ?? 9,
          default: t.adults?.default ?? 2,
        },
        children: {
          min: t.children?.min ?? 0,
          max: t.children?.max ?? 4,
          default: t.children?.default ?? [],
          ageMin: t.children?.ageMin ?? 0,
          ageMax: t.children?.ageMax ?? 17,
        },
      },
      rooms => {
        this.updateRoomButton(rooms);
      }
    );

    // Set focus tracking on all Search buttons
    this.destinationButton.setAttribute(
      'onfocus',
      `window.pushEventToDataLayer("Search Interaction", "Destination Button Focused", "Destination Button")`
    );
    this.airportButton.setAttribute(
      'onfocus',
      `window.pushEventToDataLayer("Search Interaction", "Airport Button Focused", "Airport Button")`
    );
    this.durationButton.setAttribute(
      'onfocus',
      `window.pushEventToDataLayer("Search Interaction", "Duration Button Focused", "Duration Button")`
    );
    this.dateButton.setAttribute(
      'onfocus',
      `window.pushEventToDataLayer("Search Interaction", "Date Button Focused", "Date Button")`
    );
    this.roomsButton.setAttribute(
      'onfocus',
      `window.pushEventToDataLayer("Search Interaction", "Room Button Focused", "Room Button")`
    );

    // Any Remembered Search was normally already written into the inputs the
    // defaults path reads, before first paint, by the early script the macro
    // inlines — so it is applied exactly as authored values are and, being
    // before `initialized`, fires no "criterion changed" events. This bundle's
    // storage contract has the last word in both directions, because the
    // early script is built with the default lifetime and cannot know the
    // bundle's setting:
    // - a shortened lifetime: when the bundle offers nothing, the page's own
    //   values go back;
    // - a lengthened one: an entry the bundle offers but the early script
    //   treated as lapsed (it keeps the link's authored href aside only when
    //   it applies) is applied here instead, through the same step, before
    //   the fall-back and defaults paths read the inputs.
    const applied = this.searchButton.dataset.authoredHref !== undefined;
    const read = readRememberedSearch(i18n.getLocale());
    if (!read) withdrawRememberedSearch(container);
    else if (!applied) {
      applyRememberedSearch(container, read, (key, params) =>
        i18n.t(key, params)
      );
    }

    // A remembered choice the filters no longer offer falls back to the
    // page's own value (and, through the defaults path, to the component's
    // default), one criterion at a time.
    this.droppedCriteria.push(
      ...fallBackUnavailableCriteria(container, {
        destination: id => this.destinationModal.offers(id),
        departure: code => this.airportModal.offers(code),
        duration: value => this.durationModal.offers(value),
      })
    );

    // Load defaults from data attributes
    this.loadDefaults();
    this.refreshFallenBackButtons();
    if (read) this.reportRememberedSearchApplied(read);
    this.initialized = true;

    // Committing a search remembers it. Every way of following the link
    // counts — click (pointer or keyboard), middle-click, and the context
    // menu that opens it in a new tab — and none of them is prevented.
    for (const type of ['click', 'auxclick', 'contextmenu']) {
      this.searchButton.addEventListener(type, () => this.rememberSearch());
    }

    // Event listeners
    this.toggleSearchButton.addEventListener(
      'click',
      debounce(() => {
        this.userToggledManually = true;
        this.setFiltersOpen(!this.isOpen);

        // Show backdrop when opening filters while scrolled down on mobile
        if (
          this.isOpen &&
          this.mobileQuery.matches &&
          window.scrollY > SCROLL_COLLAPSE_THRESHOLD
        ) {
          this.showBackdrop(true);
        } else {
          this.showBackdrop(false);
        }

        if (this.isOpen) {
          this.toggleSearchButton.setAttribute(
            'onpointerdown',
            `window.pushEventToDataLayer("Accordion Interaction", "Search Accordion Close", "Search Accordion")`
          );
        } else {
          this.toggleSearchButton.setAttribute(
            'onpointerdown',
            `window.pushEventToDataLayer("Accordion Interaction", "Search Accordion Open", "Search Accordion")`
          );
        }
      }, 100)
    );

    this.destinationButton.addEventListener('click', () =>
      this.destinationModal.open()
    );
    this.airportButton.addEventListener('click', () =>
      this.airportModal.open()
    );
    this.durationButton.addEventListener('click', () =>
      this.durationModal.open()
    );
    this.dateButton.addEventListener('click', () => this.dateModal.open());
    this.roomsButton.addEventListener('click', () => this.roomModal.open());

    // Close filters when clicking the backdrop overlay
    this.backdrop?.addEventListener('click', () => {
      this.setFiltersOpen(false);
      this.showBackdrop(false);
    });

    // Sticky scroll behaviour (mobile only)
    this.initStickyScrollBehaviour();

    // Hide search z-index when mobile menu is open so menu overlays search
    this.observeMobileMenu();
  }

  /**
   * Show or hide the backdrop overlay behind the search component.
   * Used when filters are expanded while scrolled down on mobile.
   */
  private showBackdrop(visible: boolean): void {
    if (!this.backdrop) return;
    if (visible) {
      this.backdrop.removeAttribute('hidden');
    } else {
      this.backdrop.setAttribute('hidden', '');
    }
  }

  /**
   * When the mobile menu is open, remove z-index from the search section
   * so the menu overlay can appear on top. Restore when menu closes.
   */
  private observeMobileMenu(): void {
    const mobileMenu = document.querySelector('[data-mobile-menu]');
    if (!mobileMenu) return;

    const section = this.container;

    const observer = new MutationObserver(() => {
      const menuOpen = !mobileMenu.hasAttribute('hidden');
      if (menuOpen) {
        section.style.zIndex = 'auto';
      } else {
        section.style.zIndex = '';
      }
    });

    observer.observe(mobileMenu, {
      attributes: true,
      attributeFilter: ['hidden'],
    });
  }

  /**
   * On mobile, auto-collapse the search filters when the user scrolls down
   * past a threshold, and auto-expand them when they scroll back to the top.
   * Manual toggle by the user takes priority until they scroll back to top.
   */
  private initStickyScrollBehaviour(): void {
    let ticking = false;
    const containerDiv = this.container.querySelector(
      '.container'
    ) as HTMLElement;
    const searchComponentEl = this.container.querySelector(
      '.search-component'
    ) as HTMLElement;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        ticking = false;

        const scrollY = window.scrollY;
        const isMobile = this.mobileQuery.matches;

        this.trackScrollDepth();

        if (scrollY <= 10) {
          // User is back at the top: restore full border-radius and horizontal
          // padding. px-0 is only meaningful for the collapsed mobile header,
          // so always remove it here to avoid a stale edge-to-edge container
          // on desktop.
          searchComponentEl.style.borderRadius = '';
          containerDiv.classList.remove('px-0');

          if (isMobile) {
            this.userToggledManually = false;
            if (!this.isOpen) {
              this.setFiltersOpen(true);
            }
            this.showBackdrop(false);
          }
        } else if (scrollY > SCROLL_COLLAPSE_THRESHOLD) {
          // Scrolled past threshold: bottom-only border-radius and edge-to-edge
          // container on all viewports.
          searchComponentEl.style.borderRadius = '0 0 1rem 1rem';
          containerDiv.classList.add('px-0');

          if (isMobile) {
            // Collapse filters unless user manually opened
            if (this.isOpen && !this.userToggledManually) {
              this.setFiltersOpen(false);
            }
          }
        }
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /**
   * Report scroll depth to analytics in 25% buckets (25/50/75/100). Each bucket
   * fires at most once per page load so we preserve scroll-depth tracking
   * without flooding the dataLayer as the user scrolls back and forth.
   */
  private trackScrollDepth(): void {
    const doc = document.documentElement;
    const scrollable = doc.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;

    const percent = Math.min(
      100,
      Math.round((window.scrollY / scrollable) * 100)
    );
    const bucket = Math.floor(percent / 25) * 25;

    if (bucket > this.maxScrollDepthReported && bucket >= 25) {
      this.maxScrollDepthReported = bucket;
      window.pushEventToDataLayer(
        'Search Interaction',
        'Scroll Depth',
        `${bucket}%`,
        {
          depth: bucket,
          device: this.mobileQuery.matches ? 'mobile' : 'desktop',
        }
      );
    }
  }

  /**
   * Programmatically open or close the search filters (shared logic between
   * scroll-triggered and manual toggle).
   */
  private setFiltersOpen(open: boolean): void {
    this.isOpen = open;
    const filters = this.container.querySelector(
      '.search-component__filters'
    ) as HTMLElement;

    if (open) {
      this.toggleSearchButton.classList.remove('btn-primary');
      this.toggleSearchButton.classList.add('underline');
      this.toggleSearchButton.textContent = i18n.t('search.closeSearch');
      this.toggleSearchButton.ariaLabel = i18n.t('search.closeSearchAria');
      filters.classList.add('search-component__filters--open');
    } else {
      this.toggleSearchButton.classList.remove('underline');
      this.toggleSearchButton.classList.add('btn-primary');
      this.toggleSearchButton.textContent = i18n.t('search.search');
      this.toggleSearchButton.ariaLabel = i18n.t('search.openSearchAria');
      filters.classList.remove('search-component__filters--open');
    }
  }

  private loadDefaults(): void {
    // Load destination defaults
    const destinationIds = this.destinationButton.dataset.selectedDestinations;
    if (destinationIds) {
      const ids = destinationIds
        .split(',')
        .map(id => parseInt(id.trim()))
        .filter(id => !isNaN(id));
      if (ids.length > 0) {
        this.destinationModal.setSelectedDestinationIds(ids);
        this.updateDestinationButton(this.destinationModal.getSelection());
      }
    }

    // Load hotel defaults. Hotels are not in the destination tree, so they
    // carry their own names.
    const hotels = this.selectedAccommodations();
    if (hotels.length > 0) {
      this.destinationModal.setSelection([
        ...this.destinationModal.getSelection(),
        ...hotels.map(hotel => ({ ...hotel, isHotel: true })),
      ]);
      this.updateDestinationButton(this.destinationModal.getSelection());
    }

    // Load airport defaults
    const airportCodes = this.airportButton.dataset.selectedAirports;
    if (airportCodes) {
      const codes = airportCodes.split(',').map(code => code.trim());
      this.airportModal.setSelection(codes);
      this.updateAirportButton(this.airportModal.getSelection());
    }

    // Load duration defaults
    const durationValue = this.durationButton.dataset.selectedDuration;
    if (durationValue) {
      this.durationModal.setSelection(parseInt(durationValue));
      this.updateDurationButton(this.durationModal.getSelection());
    }

    // Load date defaults: a whole month, or a date with its flexibility. Only
    // a Remembered Search writes them — dates are never authored — so dates
    // the departure window can no longer offer fall back to "any".
    const selectedDate = this.dateButton.dataset.selectedDate;
    if (selectedDate) {
      // The shortest stay bounds the latest departure; the duration above
      // may just have changed it.
      this.refreshDateWindow();
      const flexibility = parseInt(this.dateButton.dataset.flexibility ?? '');
      const restored =
        this.dateButton.dataset.wholeMonth === 'true'
          ? this.dateModal.restoreWholeMonth(selectedDate.slice(0, 7))
          : this.dateModal.restoreDate(
              selectedDate,
              Number.isNaN(flexibility) ? null : flexibility
            );
      if (!restored) this.droppedCriteria.push('dates');
      this.updateDateButton(this.dateModal.getSelection());
    }

    // Load room defaults
    const roomsData = this.roomsButton.dataset.selectedRooms;
    if (roomsData) {
      try {
        const parsedData = JSON.parse(roomsData);
        // Handle both single room object and array of rooms
        const parsedRooms = Array.isArray(parsedData)
          ? parsedData
          : [parsedData];
        const rooms = parsedRooms.map((room: any) => ({
          adults: room.travellersAdults,
          children: room.travellersChildrenAge?.length || 0,
          childAges: room.travellersChildrenAge || [],
        }));
        this.roomModal.setRooms(rooms);
        this.updateRoomButton(rooms);
      } catch (e) {
        console.error('Failed to parse room data:', e);
      }
    }
  }

  /**
   * The defaults path skips an empty input, which would leave a fallen-back
   * criterion showing its remembered label; relabel each from its selection.
   */
  private refreshFallenBackButtons(): void {
    // Dates relabel themselves in the defaults path (`updateDateButton`).
    for (const criterion of this.droppedCriteria) {
      if (criterion === 'destination') {
        this.updateDestinationButton(this.destinationModal.getSelection());
      } else if (criterion === 'departure') {
        this.updateAirportButton(this.airportModal.getSelection());
      } else if (criterion === 'duration') {
        this.updateDurationButton(this.durationModal.getSelection());
      }
    }
  }

  /**
   * Report that a Remembered Search was applied — which criteria it restored
   * and which the page could no longer offer — and keep the query string it
   * produced as the baseline the Search click's `prefilled` flag compares to.
   * A pinned criterion keeps the page's value, so is not counted as restored,
   * but is part of the baseline.
   */
  private reportRememberedSearchApplied(remembered: RememberedSearch): void {
    const controls: Array<[RememberedCriterion, HTMLElement, boolean]> = [
      ['destination', this.destinationButton, true],
      ['departure', this.airportButton, true],
      ['duration', this.durationButton, true],
      ['dates', this.dateButton, true],
      // An empty room list is not applied (the page's party stays).
      ['rooms', this.roomsButton, remembered.rooms.length > 0],
    ];
    const dropped = [...this.droppedCriteria];
    const restored = controls
      .filter(
        ([criterion, button, applied]) =>
          applied &&
          !button.hasAttribute('data-pinned') &&
          !dropped.includes(criterion as DroppedCriterion)
      )
      .map(([criterion]) => criterion);

    window.pushEventToDataLayer(
      'Search Interaction',
      'Remembered Search Applied',
      'Search Component',
      { restored, dropped }
    );

    this.prefilledQueryString = this.queryString;
    this.updateSearchButton();
  }

  /** The `data-selected-accommodations` input: `[{ id, name }]`, or none. */
  private selectedAccommodations(): Array<{ id: number; name: string }> {
    const raw = this.destinationButton.dataset.selectedAccommodations;
    if (!raw) return [];
    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.filter(
            (hotel): hotel is { id: number; name: string } =>
              typeof hotel?.id === 'number' && typeof hotel?.name === 'string'
          )
        : [];
    } catch {
      return [];
    }
  }

  /** Remember the search the visitor is committing, as chosen. */
  private rememberSearch(): void {
    const destination = this.destinationModal.getSelection();
    const departure = this.airportModal.getSelection();
    const duration = this.durationModal.getSelection();

    writeRememberedSearch(i18n.getLocale(), {
      destination:
        destination.length > 0
          ? destination.map(({ id, name, isHotel }) => ({
              id,
              name,
              isHotel: isHotel === true,
            }))
          : 'any',
      departure:
        departure.length > 0
          ? departure.map(({ code, name, isGroup }) => ({
              code: String(code),
              name,
              isGroup: isGroup === true,
            }))
          : 'any',
      duration: duration
        ? { value: duration.value, name: duration.name }
        : 'any',
      dates: this.chosenDates(),
      rooms: this.roomModal.getRooms().map(room => ({
        adults: room.adults,
        childAges: [...room.childAges],
      })),
    });
  }

  /** The departure dates as chosen — a whole month, or a date and its flexibility. */
  private chosenDates(): RememberedDates {
    const { isWholeMonth, minDate, selectedDate, flexibility } =
      this.dateModal.getSelection();
    if (isWholeMonth && minDate) {
      return {
        kind: 'month',
        month: minDate.slice(0, 7),
        label: this.formatMonth(minDate),
      };
    }
    if (selectedDate) {
      return {
        kind: 'date',
        date: selectedDate,
        flexibility: flexibility || null,
        label: this.formatDate(selectedDate),
      };
    }
    return 'any';
  }

  private updateDestinationButton(
    selected: Array<{ id: number; name: string; isHotel?: boolean }>
  ): void {
    const currentDestination =
      this.destinationButton.dataset.selectedDestinations;
    const updatedDestination = selected.map(d => d.id).join(',');
    const changed = updatedDestination !== currentDestination;

    this.destinationButton.dataset.selectedDestinations = updatedDestination;

    if (changed && this.initialized) {
      window.pushEventToDataLayer(
        'Search Interaction',
        'Destination Changed',
        'Destination Button'
      );
    }

    let destinationText = i18n.t('search.anyDestination');
    if (selected.length > 1) {
      destinationText = `${selected[0].name} + ${selected.length - 1}`;
    } else if (selected.length === 1) {
      destinationText = selected[0].name;
    }

    this.destinationButton.querySelector('span')!.textContent = destinationText;
    this.destinationButton.ariaLabel = i18n
      .t('search.selectDestinationAria')
      .replace(
        '{destination}',
        selected.length > 0
          ? selected.map(d => d.name).join(', ')
          : i18n.t('search.anyDestination')
      );
    this.updateSearchButton();
  }

  private updateAirportButton(
    selected: Array<{ code: string | number; name: string; isGroup?: boolean }>
  ): void {
    const currentAirports = this.airportButton.dataset.selectedAirports;
    const updatedAirports = selected.map(d => d.code).join(',');
    const changed = updatedAirports !== currentAirports;

    this.airportButton.dataset.selectedAirports = updatedAirports;

    if (changed && this.initialized) {
      window.pushEventToDataLayer(
        'Search Interaction',
        'Airport Changed',
        'Airport Button'
      );
    }

    let airportText = i18n.t('search.anyAirport');
    if (selected.length > 1) {
      airportText = `${selected[0].name} + ${selected.length - 1}`;
    } else if (selected.length === 1) {
      airportText = selected[0].name;
    }

    this.airportButton.querySelector('span')!.textContent = airportText;
    this.airportButton.ariaLabel = i18n
      .t('search.selectAirportAria')
      .replace(
        '{airport}',
        selected.length > 0
          ? selected.map(d => d.name).join(', ')
          : i18n.t('search.anyAirport')
      );

    this.updateSearchButton();
  }

  private updateDurationButton(
    selected: { value: number; name: string } | null
  ): void {
    const currentDuration = this.durationButton.dataset.selectedDuration;
    const updatedDuration = selected ? selected.value.toString() : '';
    const changed = updatedDuration !== currentDuration;

    if (selected) {
      this.durationButton.dataset.selectedDuration = updatedDuration;
      this.durationButton.querySelector('span')!.textContent = selected.name;
    } else {
      this.durationButton.dataset.selectedDuration = '';
      this.durationButton.querySelector('span')!.textContent =
        i18n.t('search.anyDuration');
    }

    if (changed && this.initialized) {
      window.pushEventToDataLayer(
        'Search Interaction',
        'Duration Changed',
        'Duration Button'
      );
    }

    this.durationButton.ariaLabel = i18n
      .t('search.selectDurationAria')
      .replace('{duration}', selected?.name || i18n.t('search.anyDuration'));

    this.updateSearchButton();
  }

  private updateDateButton(selected: {
    minDate: string | null;
    maxDate: string | null;
    selectedDate: string | null;
    flexibility: number | null;
    isWholeMonth: boolean;
  }): void {
    const currentMinDate = this.dateButton.dataset.minDate;
    const currentMaxDate = this.dateButton.dataset.maxDate;
    const updatedMinDate = selected.minDate || '';
    const updatedMaxDate = selected.maxDate || '';
    const changed =
      updatedMinDate !== currentMinDate || updatedMaxDate !== currentMaxDate;

    this.dateButton.dataset.minDate = updatedMinDate;
    this.dateButton.dataset.maxDate = updatedMaxDate;
    this.dateButton.dataset.selectedDate = selected.isWholeMonth
      ? updatedMinDate
      : selected.selectedDate || '';
    this.dateButton.dataset.flexibility =
      selected.flexibility === null ? '' : String(selected.flexibility);
    this.dateButton.dataset.wholeMonth = selected.isWholeMonth ? 'true' : '';

    let type = 'empty';
    let dateText = i18n.t('search.anyDate');
    let formattedDate = '';

    if (selected.isWholeMonth && selected.minDate) {
      type = 'month';
      dateText = this.formatMonth(selected.minDate);
      formattedDate = dateText;
    } else if (
      !selected.isWholeMonth &&
      selected.selectedDate &&
      selected.flexibility !== null
    ) {
      type = 'flexible';
      const flexText = i18n.t('modal.date.flexibleDays', {
        days: selected.flexibility,
      });
      formattedDate = this.formatDate(selected.selectedDate);
      dateText = `${formattedDate} ${flexText}`;
    } else if (selected.selectedDate) {
      type = 'other';
      formattedDate = this.formatDate(selected.selectedDate);
      dateText = formattedDate;
    }

    this.dateButton.querySelector('span')!.textContent = dateText;
    this.dateButton.ariaLabel = i18n.t('search.selectDateAria', {
      type,
      date: formattedDate,
      flexibility: selected.flexibility || 0,
    });

    if (changed && this.initialized) {
      window.pushEventToDataLayer(
        'Search Interaction',
        'Date Changed',
        'Date Button'
      );
    }

    this.updateSearchButton();
  }

  private updateRoomButton(
    rooms: Array<{ adults: number; children: number; childAges: number[] }>
  ): void {
    const currentRooms = this.roomsButton.dataset.selectedRooms;
    const selectedRooms = rooms.map(room => ({
      travellersAdults: room.adults,
      travellersChildrenAge: room.childAges,
    }));
    const updatedRooms = JSON.stringify(selectedRooms);
    const changed = updatedRooms !== currentRooms;

    this.roomsButton.dataset.selectedRooms = updatedRooms;

    if (changed && this.initialized) {
      window.pushEventToDataLayer(
        'Search Interaction',
        'Room Changed',
        'Room Button'
      );
    }

    const roomText =
      rooms.length > 1 ? i18n.t('search.rooms') : i18n.t('search.room');
    let buttonText = `${rooms.length} ${roomText}`;
    const totals = this.roomModal.getTotalCounts();

    if (totals.adults > 0) {
      const adultsText =
        totals.adults > 1 ? i18n.t('search.adults') : i18n.t('search.adult');
      buttonText += `: ${totals.adults} ${adultsText}`;
    }

    if (totals.children > 0) {
      const childrenText =
        totals.children > 1
          ? i18n.t('search.children')
          : i18n.t('search.child');
      buttonText += `, ${totals.children} ${childrenText}`;
    }

    this.roomsButton.querySelector('span')!.textContent = buttonText;
    this.roomsButton.ariaLabel = i18n.t('search.selectRoomAria', {
      rooms: 1,
      adults: totals.adults,
      children: totals.children,
    });

    this.updateSearchButton();
  }

  private updateSearchButton(): void {
    // Built by the same function the early script uses for the link it sets
    // before this component loads, so the two cannot send a click elsewhere.
    const destinationSelection = this.destinationModal.getSelection();
    const airportSelection = this.airportModal.getSelection();
    const dateSelection = this.dateModal.getSelection();
    const hasDates =
      (dateSelection.selectedDate || dateSelection.isWholeMonth) &&
      dateSelection.minDate &&
      dateSelection.maxDate;

    const queryString = resultsQuery({
      destinationIds: destinationSelection
        .filter(d => !d.isHotel)
        .map(d => d.id),
      hotelIds: destinationSelection.filter(d => d.isHotel).map(d => d.id),
      airportGroupIds: airportSelection.filter(a => a.isGroup).map(a => a.code),
      airportCodes: airportSelection.filter(a => !a.isGroup).map(a => a.code),
      duration: this.durationModal.getSelection()?.value ?? null,
      dates: hasDates
        ? { min: dateSelection.minDate!, max: dateSelection.maxDate! }
        : null,
      rooms: this.roomModal.getRooms(),
      options: this.configOptions,
    });
    const siteUrl = (
      process.env.SITE_URL ||
      `https://${process.env.ENVIRONMENT || 'dev'}.nonprod-sundeals.com`
    ).replace(/\/$/, '');
    this.searchButton.setAttribute(
      'href',
      `${siteUrl}/${i18n.getLocale().toLowerCase()}/results?${queryString}`
    );

    this.queryString = queryString;
    const prefilled =
      this.prefilledQueryString !== null &&
      queryString === this.prefilledQueryString;
    this.searchButton.setAttribute(
      'onpointerdown',
      `window.pushEventToDataLayer("Search Interaction", "Click", "Search Button", ${JSON.stringify(prefilled ? { queryString, prefilled } : { queryString })})`
    );
  }

  /** "June 2027", for the month `dateStr` falls in. */
  private formatMonth(dateStr: string): string {
    const date = parseLocalDate(dateStr);
    const monthName = i18n.t(`modal.date.monthsFull.${date.getMonth() + 1}`);
    return `${monthName} ${date.getFullYear()}`;
  }

  private formatDate(dateStr: string): string {
    const date = parseLocalDate(dateStr);
    const day = date.getDate();
    const monthName = i18n.t(`modal.date.monthsFull.${date.getMonth() + 1}`);
    const year = date.getFullYear();

    // English uses ordinal suffixes (1st, 2nd, 3rd), German just uses numbers
    const locale = i18n.getLocale();
    if (locale === 'en-GB') {
      const lastDigit = day % 10;
      const lastTwoDigits = day % 100;
      let suffix = 'th';
      if (lastTwoDigits < 11 || lastTwoDigits > 13) {
        if (lastDigit === 1) suffix = 'st';
        else if (lastDigit === 2) suffix = 'nd';
        else if (lastDigit === 3) suffix = 'rd';
      }
      return `${day}${suffix} ${monthName} ${year}`;
    } else {
      // German format: "5. März 2024"
      return `${day}. ${monthName} ${year}`;
    }
  }
}

export default SearchComponent;
